import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'

// Guard de idempotencia: el agent se inicializa una sola vez por carga
// de página, sin importar cuántas veces se llame a initNewRelic(). Con el
// gate por consent (sprint 2026-06-16) la llamada puede venir desde
// main.tsx (boot inicial si ya hubo opt-in previo) o desde App.tsx (user
// acepta durante la sesión).
let initialized = false

/**
 * Inicializa el New Relic Browser Agent. Solo se activa cuando:
 *
 *   1. El build es de producción (`import.meta.env.PROD === true`). En dev
 *      / preview no hay sentido enviar telemetría real.
 *   2. Las VITE_NR_* env vars necesarias están definidas. Si falta cualquier
 *      pieza (license key, app id, account id, trust key), salimos sin
 *      inicializar — el FE arranca igual y no rompe.
 *
 * <p>Las VITE_NR_* las pone el workflow de Cloudflare Pages en build-time
 * (variables del proyecto). En local sin ellas el agent no se carga.</p>
 *
 * <p>Captura sin instrumentación adicional: page loads, AJAX, JS errors,
 * route changes (SPA). Para errores capturados manualmente desde nuestro
 * ErrorBoundary usamos {@link reportError} de abajo.</p>
 *
 * @returns true si el agent quedó inicializado, false si se saltó.
 */
export function initNewRelic(): boolean {
  if (initialized) return true
  if (!import.meta.env.PROD) return false

  const licenseKey = import.meta.env.VITE_NR_BROWSER_LICENSE_KEY as string | undefined
  const applicationID = import.meta.env.VITE_NR_BROWSER_APP_ID as string | undefined
  const accountID = import.meta.env.VITE_NR_BROWSER_ACCOUNT_ID as string | undefined
  const trustKey = import.meta.env.VITE_NR_BROWSER_TRUST_KEY as string | undefined

  if (!licenseKey || !applicationID || !accountID || !trustKey) {
    console.warn('[New Relic] missing VITE_NR_BROWSER_* envs — agent disabled')
    return false
  }

  // Beacon URL — la cuenta NR de matchplay vive en la región EU
  // (collector.eu01.nr-data.net en los logs del backend agent). Los license
  // keys modernos del Browser agent (formato NRJS-...) NO llevan el prefix
  // "eu01" — la región es propiedad de la cuenta, no de la key. Hardcodeamos
  // EU porque es la única cuenta del proyecto. Si en algún momento la cuenta
  // migra a US, cambiar a 'bam.nr-data.net'.
  const beaconUrl = 'bam.eu01.nr-data.net'

  new BrowserAgent({
    init: {
      // Session replay OFF — no está en free tier y captura interacciones
      // del usuario que no queremos almacenar sin auditoría de privacidad.
      session_replay: { enabled: false },
      distributed_tracing: { enabled: true },
      privacy: { cookies_enabled: true },
    },
    info: {
      beacon: beaconUrl,
      errorBeacon: beaconUrl,
      licenseKey,
      applicationID,
      sa: 1,
    },
    loader_config: {
      accountID,
      trustKey,
      agentID: applicationID,
      licenseKey,
      applicationID,
    },
  })
  initialized = true
  return true
}

/**
 * Reporta un error a New Relic desde código React (típico: ErrorBoundary
 * capturando excepciones del render tree).
 *
 * <p>Si el agent no se inicializó (dev local, build sin envs, etc.) la
 * llamada es no-op — el `(globalThis as any).newrelic` que el agent
 * inyecta solo existe cuando el script ha cargado.</p>
 */
export function reportError(error: Error, customAttributes?: Record<string, unknown>): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nr = (globalThis as any).newrelic
  if (nr && typeof nr.noticeError === 'function') {
    nr.noticeError(error, customAttributes)
  }
}
